import React, { useCallback } from 'react';
import { Card, Heading, Row, Text, Box, Input } from '../core';
import {
  useAsync,
  useSend,
  schedule,
  ScionTimelock,
  useInputState,
} from '@sc1/common';
import { StatusButton } from '../StatusButton';

import { ScheduleIcon } from '../icons';
import type { CardProps } from 'theme-ui';
import { Contract } from 'ethers';

interface AmountMethodProps<T, F> extends CardProps {
  contract: T | undefined;
  timelock?: ScionTimelock;
  method: F;
  label: string;
  disabled: boolean;
  decimals?: number;
  hint?: string;
  labelSubmit?: string;
}

export const AmountMethod = <T extends Contract, F extends keyof T & string>({
  contract,
  timelock,
  method,
  label,
  disabled,
  decimals,
  hint,
  labelSubmit,
  ...rest
}: AmountMethodProps<T, F>): JSX.Element => {
  const { amountStr, amountBN, setAmount } = useInputState();

  const { sendTx, txStatus } = useSend();

  const useDecimals = useCallback(async () => {
    if (decimals == null && contract && 'decimals' in contract)
      return contract?.decimals();
    return decimals;
  }, [contract && 'decimals' in contract, decimals]);
  const { res: dec } = useAsync(useDecimals, []);

  const handleSubmit = useCallback(() => {
    if (amountStr === '') return; // avoid unintentional 0 input
    if (!contract) return;
    // TODO: is there a way to typecheck that Parameters<T[F]> here is [BigNumber] ?
    const params = [amountBN] as Parameters<T[F]>;
    if (timelock) {
      return schedule(sendTx, timelock, contract, method, params);
    }
    sendTx(contract, method, params);
  }, [contract, timelock, method, amountStr, amountBN, schedule, sendTx]);

  return (
    <Card {...rest}>
      {hint && (
        <Text variant="hint" mb={1}>
          {hint}
        </Text>
      )}
      <Heading as="h3" mb={2}>
        {label}
      </Heading>
      <Row variant="collapse">
        <Input
          value={amountStr}
          disabled={disabled}
          min="0"
          placeholder="0"
          type="number"
          onChange={(e) => {
            setAmount(e.target.value, dec);
          }}
        />

        <Box my={1} mx={1} />
        <StatusButton
          disabled={disabled}
          onClick={handleSubmit}
          status={txStatus}
          suffix={timelock ? <ScheduleIcon /> : null}
        >
          {labelSubmit || 'Submit'}
        </StatusButton>
      </Row>
      {amountStr !== '' && (
        <Text sx={{ mt: 2, fontSize: 1 }}>
          Fmt Amnt: {amountBN?.toString()}
        </Text>
      )}
    </Card>
  );
};
